import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  User, MapPin, Phone, Globe, Linkedin, Instagram, Briefcase, Clock, DollarSign, Loader2, ArrowLeft
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FreelancerPublicProfile() {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("about");

  useEffect(() => {
    axios.get(`${API}/freelancers/${slug}`)
      .then((res) => setProfile(res.data))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-stone-300" /></div>;
  if (!profile) return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      <User className="w-16 h-16 text-stone-300 mx-auto mb-4" />
      <h1 className="text-2xl font-bold text-stone-900 mb-2">Profil Bulunamadı</h1>
      <p className="text-stone-500 mb-6">Bu freelancer profili mevcut değil veya henüz yayınlanmamış.</p>
      <Link to="/"><Button variant="outline">Ana Sayfaya Dön</Button></Link>
    </div>
  );

  const TABS = [
    { id: "about", label: "Hakkında" },
    { id: "portfolio", label: `Portfolyo (${(profile.portfolio || []).length})` },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Cover */}
      <div className="h-48 md:h-64 bg-gradient-to-r from-[#1B4F72] to-[#2E86AB] relative">
        {profile.cover_image && <img src={profile.cover_image} alt="" className="w-full h-full object-cover" />}
        <Link to="/" className="absolute top-4 left-4 bg-white/90 backdrop-blur text-stone-700 px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 hover:bg-white">
          <ArrowLeft className="w-4 h-4" /> Geri
        </Link>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-16 relative z-10 pb-12">
        {/* Profile Header */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-28 h-28 rounded-full border-4 border-white bg-stone-100 overflow-hidden shadow-lg flex-shrink-0 -mt-16 md:-mt-20">
              {profile.profile_image ? (
                <img src={profile.profile_image} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><User className="w-12 h-12 text-stone-300" /></div>
              )}
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-stone-900 font-serif">{profile.full_name}</h1>
              <p className="text-[#1B4F72] font-medium">{profile.title}</p>
              <div className="flex flex-wrap gap-3 mt-3 text-sm text-stone-500">
                {profile.city && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {profile.city}</span>}
                {profile.experience_years > 0 && <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {profile.experience_years} yıl deneyim</span>}
                {profile.hourly_rate && <span className="flex items-center gap-1"><DollarSign className="w-4 h-4" /> {profile.hourly_rate}</span>}
              </div>
              {profile.skills?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {profile.skills.map((skill, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{skill}</Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              {profile.phone && (
                <a href={`tel:${profile.phone}`} className="flex items-center gap-2 text-sm text-stone-600 hover:text-[#1B4F72]">
                  <Phone className="w-4 h-4" /> {profile.phone}
                </a>
              )}
              {profile.website && (
                <a href={profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-stone-600 hover:text-[#1B4F72]">
                  <Globe className="w-4 h-4" /> Website
                </a>
              )}
              {profile.linkedin && (
                <a href={profile.linkedin.startsWith("http") ? profile.linkedin : `https://${profile.linkedin}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-stone-600 hover:text-[#1B4F72]">
                  <Linkedin className="w-4 h-4" /> LinkedIn
                </a>
              )}
              {profile.instagram && (
                <a href={`https://instagram.com/${profile.instagram.replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-stone-600 hover:text-[#1B4F72]">
                  <Instagram className="w-4 h-4" /> {profile.instagram}
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-lg p-1 border border-stone-200 w-fit">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id ? "bg-[#1B4F72] text-white" : "text-stone-600 hover:bg-stone-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* About Tab */}
        {activeTab === "about" && profile.bio && (
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <h2 className="font-semibold text-stone-900 mb-4">Hakkında</h2>
            <p className="text-stone-600 whitespace-pre-line">{profile.bio}</p>
          </div>
        )}

        {/* Portfolio Tab */}
        {activeTab === "portfolio" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(profile.portfolio || []).map((item) => (
              <div key={item.id} className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                {item.images?.[0] && (
                  <img src={item.images[0]} alt="" className="w-full h-48 object-cover" />
                )}
                <div className="p-4">
                  <h3 className="font-semibold text-stone-900">{item.title}</h3>
                  <p className="text-xs text-stone-400 mt-1">{item.client} · {item.year}</p>
                  {item.category && <Badge variant="outline" className="mt-2 text-xs">{item.category}</Badge>}
                  {item.description && <p className="text-sm text-stone-600 mt-3">{item.description}</p>}
                  {item.images?.length > 1 && (
                    <div className="flex gap-1 mt-3">
                      {item.images.slice(1, 4).map((img, i) => (
                        <img key={i} src={img} alt="" className="w-12 h-12 object-cover rounded" />
                      ))}
                      {item.images.length > 4 && <span className="w-12 h-12 bg-stone-100 rounded flex items-center justify-center text-xs text-stone-500">+{item.images.length - 4}</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {(!profile.portfolio || profile.portfolio.length === 0) && (
              <div className="col-span-2 text-center py-12 text-stone-400">
                <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Henüz portfolyo projesi eklenmedi</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
